import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

const getDisplayName = (agent) => {
  const name = `${agent.first_name || ''} ${agent.last_name || ''}`.trim();
  return name || agent.email || agent._id;
};

const AgentListPanel = ({ title, agents, selected, onSelect }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      borderRadius: 2,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box
      sx={{
        px: 2,
        py: 1.5,
        textAlign: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Typography variant="subtitle2" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {agents.length} {agents.length === 1 ? 'agente' : 'agentes'}
      </Typography>
    </Box>

    <List dense disablePadding sx={{ flexGrow: 1, maxHeight: 300, overflow: 'auto' }}>
      {agents.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Sin agentes
          </Typography>
        </Box>
      ) : (
        agents.map((agent) => (
          <ListItemButton
            key={agent._id}
            selected={selected?._id === agent._id}
            onClick={() => onSelect(selected?._id === agent._id ? null : agent)}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { backgroundColor: 'primary.dark' },
                '& .MuiListItemText-secondary': {
                  color: 'primary.contrastText',
                  opacity: 0.8,
                },
              },
            }}
          >
            <ListItemText
              primary={getDisplayName(agent)}
              secondary={agent.email}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </ListItemButton>
        ))
      )}
    </List>
  </Paper>
);

export const GroupAgentsTransfer = ({
  availableAgents = [],
  groupAgents = [],
  onUpdate,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [available, setAvailable] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [selectedAvailable, setSelectedAvailable] = useState(null);
  const [selectedAssigned, setSelectedAssigned] = useState(null);

  useEffect(() => {
    setAvailable([...availableAgents]);
    setAssigned([...groupAgents]);
    setSelectedAvailable(null);
    setSelectedAssigned(null);
  }, [availableAgents, groupAgents]);

  const moveToAssigned = () => {
    if (!selectedAvailable) return;
    setAssigned((prev) =>
      prev.some((a) => a._id === selectedAvailable._id) ? prev : [...prev, selectedAvailable]
    );
    setAvailable((prev) => prev.filter((a) => a._id !== selectedAvailable._id));
    setSelectedAvailable(null);
  };

  const moveToAvailable = () => {
    if (!selectedAssigned) return;
    setAvailable((prev) =>
      prev.some((a) => a._id === selectedAssigned._id) ? prev : [...prev, selectedAssigned]
    );
    setAssigned((prev) => prev.filter((a) => a._id !== selectedAssigned._id));
    setSelectedAssigned(null);
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(103, 58, 183, 0.12)'
          : '#F3E5F5',
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2,
          alignItems: isMobile ? 'stretch' : 'center',
          mb: 3,
        }}
      >
        <AgentListPanel
          title="Agentes disponibles"
          agents={available}
          selected={selectedAvailable}
          onSelect={setSelectedAvailable}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'row' : 'column',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton
            onClick={moveToAssigned}
            disabled={!selectedAvailable}
            sx={{
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { backgroundColor: 'grey.100' },
              '&.Mui-disabled': { opacity: 0.4, backgroundColor: 'background.paper' },
            }}
          >
            {isMobile ? <ArrowDownwardIcon /> : <ArrowForwardIcon />}
          </IconButton>

          <IconButton
            onClick={moveToAvailable}
            disabled={!selectedAssigned}
            sx={{
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { backgroundColor: 'grey.100' },
              '&.Mui-disabled': { opacity: 0.4, backgroundColor: 'background.paper' },
            }}
          >
            {isMobile ? <ArrowUpwardIcon /> : <ArrowBackIcon />}
          </IconButton>
        </Box>

        <AgentListPanel
          title="Agentes en el grupo"
          agents={assigned}
          selected={selectedAssigned}
          onSelect={setSelectedAssigned}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={() => onUpdate(assigned)}
          disabled={loading}
          sx={{ minWidth: 160, borderRadius: 2, py: 1.2 }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Actualizar'}
        </Button>
      </Box>
    </Box>
  );
};
